import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../api/client';
import type { OrgRoleRow, PermissionCatalogModule } from './superOrgTypes';
import { superOrgBtn, superOrgField, superOrgInput } from './superOrgPanels';
import type { SuperOrgDetailOutletContext } from './SuperOrgDetailLayout';

type RoleDetail = OrgRoleRow & { permissions: string[] };

export function SuperOrgRolesPage() {
  const { org, token, reload: reloadOrg, setPageErr } = useOutletContext<SuperOrgDetailOutletContext>();
  const [roles, setRoles] = useState<OrgRoleRow[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalogModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<RoleDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [formName, setFormName] = useState('');
  const [formKey, setFormKey] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formPerms, setFormPerms] = useState<Set<string>>(new Set());

  const loadRoles = useCallback(async () => {
    const data = await apiGet<OrgRoleRow[]>(`/api/super/organizations/${org.id}/roles`, token);
    setRoles(data);
  }, [org.id, token]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setPageErr('');
      try {
        const cat = await apiGet<{ modules: PermissionCatalogModule[] }>(
          `/api/super/organizations/${org.id}/permission-catalog`,
          token
        );
        setCatalog(cat.modules);
        await loadRoles();
      } catch (ex) {
        setPageErr((ex as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [org.id, token, loadRoles, setPageErr]);

  const webModules = useMemo(() => catalog.filter((m) => m.surface === 'web'), [catalog]);
  const pwaModules = useMemo(() => catalog.filter((m) => m.surface === 'pwa'), [catalog]);

  function openCreate() {
    setCreating(true);
    setEditing(null);
    setFormName('');
    setFormKey('');
    setFormDesc('');
    setFormPerms(new Set());
  }

  async function openEdit(roleId: string) {
    setPageErr('');
    const data = await apiGet<RoleDetail>(`/api/super/organizations/${org.id}/roles/${roleId}`, token);
    setEditing(data);
    setCreating(false);
    setFormName(data.name);
    setFormKey(data.key);
    setFormDesc(data.description ?? '');
    setFormPerms(new Set(data.permissions));
  }

  function togglePerm(key: string) {
    setFormPerms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function saveRole() {
    setPageErr('');
    const permissions = [...formPerms];
    try {
      if (creating) {
        await apiPost(
          `/api/super/organizations/${org.id}/roles`,
          { key: formKey.trim() || undefined, name: formName.trim(), description: formDesc.trim() || null, permissions },
          token,
          { successMessage: 'Rol creado' }
        );
      } else if (editing) {
        await apiPatch(
          `/api/super/organizations/${org.id}/roles/${editing.id}`,
          { name: formName.trim(), description: formDesc.trim() || null, permissions },
          token,
          { successMessage: 'Rol actualizado' }
        );
      }
      setEditing(null);
      setCreating(false);
      await loadRoles();
      await reloadOrg();
    } catch (ex) {
      setPageErr((ex as Error).message);
    }
  }

  async function cloneRole(roleId: string) {
    setPageErr('');
    try {
      await apiPost(`/api/super/organizations/${org.id}/roles/${roleId}/clone`, {}, token, {
        successMessage: 'Rol clonado',
      });
      await loadRoles();
    } catch (ex) {
      setPageErr((ex as Error).message);
    }
  }

  async function deleteRole(role: OrgRoleRow) {
    if (!window.confirm(`¿Eliminar el rol "${role.name}"?`)) return;
    setPageErr('');
    try {
      await apiDelete(`/api/super/organizations/${org.id}/roles/${role.id}`, token, {
        successMessage: 'Rol eliminado',
      });
      await loadRoles();
    } catch (ex) {
      setPageErr((ex as Error).message);
    }
  }

  const showForm = creating || editing;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 17, margin: 0, color: '#0f172a' }}>Roles y permisos</h2>
        <button type="button" style={superOrgBtn} onClick={openCreate}>
          Nuevo rol
        </button>
      </div>

      {loading ?
        <p style={{ color: '#64748b' }}>Cargando…</p>
      : <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f8fafc', color: '#64748b' }}>
                <th style={{ padding: '10px 14px' }}>Nombre</th>
                <th style={{ padding: '10px 14px' }}>Clave</th>
                <th style={{ padding: '10px 14px' }}>Tipo</th>
                <th style={{ padding: '10px 14px' }}>Permisos</th>
                <th style={{ padding: '10px 14px' }}>Usuarios</th>
                <th style={{ padding: '10px 14px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px' }}>{r.name}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>{r.key}</td>
                  <td style={{ padding: '10px 14px' }}>{r.isSystem ? 'Sistema' : 'Custom'}</td>
                  <td style={{ padding: '10px 14px' }}>{r.permissionCount}</td>
                  <td style={{ padding: '10px 14px' }}>{r.userCount}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button type="button" style={{ ...superOrgBtn, fontSize: 12, padding: '6px 10px' }} onClick={() => void openEdit(r.id)}>
                        Editar
                      </button>
                      <button type="button" style={{ ...superOrgBtn, fontSize: 12, padding: '6px 10px', background: '#64748b' }} onClick={() => void cloneRole(r.id)}>
                        Clonar
                      </button>
                      {!r.isSystem ?
                        <button
                          type="button"
                          style={{ ...superOrgBtn, fontSize: 12, padding: '6px 10px', background: '#b91c1c' }}
                          onClick={() => void deleteRole(r)}
                        >
                          Eliminar
                        </button>
                      : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}

      {showForm ?
        <div
          style={{
            marginTop: 24,
            padding: 20,
            background: '#fff',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
          }}
        >
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>{creating ? 'Nuevo rol' : `Editar: ${editing?.name}`}</h3>
          <label style={superOrgField}>
            Nombre
            <input style={superOrgInput} value={formName} onChange={(e) => setFormName(e.target.value)} />
          </label>
          {creating ?
            <label style={superOrgField}>
              Clave (slug)
              <input style={superOrgInput} value={formKey} onChange={(e) => setFormKey(e.target.value)} placeholder="mi_rol_custom" />
            </label>
          : null}
          <label style={superOrgField}>
            Descripción
            <input style={superOrgInput} value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
          </label>

          <PermissionGrid title="Consola web" modules={webModules} selected={formPerms} onToggle={togglePerm} />
          <PermissionGrid title="PWA empresa" modules={pwaModules} selected={formPerms} onToggle={togglePerm} />

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button type="button" style={superOrgBtn} onClick={() => void saveRole()}>
              Guardar
            </button>
            <button
              type="button"
              style={{ ...superOrgBtn, background: '#64748b' }}
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      : null}
    </div>
  );
}

function PermissionGrid({
  title,
  modules,
  selected,
  onToggle,
}: {
  title: string;
  modules: PermissionCatalogModule[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#334155' }}>{title}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {modules.map((m) => (
          <div key={`${m.surface}.${m.module}`} style={{ border: '1px solid #f1f5f9', borderRadius: 8, padding: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{m.label}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {m.actions.map((action) => {
                const key = `${m.surface}.${m.module}.${action}`;
                return (
                  <label key={key} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="checkbox" checked={selected.has(key)} onChange={() => onToggle(key)} />
                    {action}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
